import React, { Suspense } from 'react';
import CustomThemeProvider from './configuration/CustomThemeProvider';
import RouteConfiguration from './routes/RouteConfiguration';
import { Box, Typography } from '@mui/material';
import { Authenticator, View } from '@aws-amplify/ui-react';
import store from './store/Store';
import { Provider } from 'react-redux';
import { PermissionsProvider } from './context/PermissionsContext/PermissionsContext';
import { ThemeProvider } from './context/PermissionsContext/ThemeContext/ThemeContext';
import './configuration/AmplifyConfiguration';
import '@aws-amplify/ui-react/styles.css';
import './Application.css';
import ApplicationStateBootstrap from './configuration/ApplicationStateBootstrap';
import './Internationalization';
import CenteredProgress from './components/CenteredProgress';
import { ToastContainer, toast } from 'react-toastify';
import styled from '@emotion/styled';
import 'react-toastify/dist/ReactToastify.minimal.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
const StyledToastContainer = styled(ToastContainer)`
  // https://styled-components.com/docs/faqs#how-can-i-override-styles-with-higher-specificity
  &&&.Toastify__toast-container {
    background-color: transparent;
    box-shadow: none;
    padding: 0;
  }
  .Toastify__toast {
    background-color: transparent;
    box-shadow: none;
    padding: 0;
  }
  .Toastify__toast-body {
    background-color: transparent;
    box-shadow: none;
    padding: 0;
  }
  .Toastify__progress-bar {
    margin: 0px;
    padding: 0px;
    border-radius: 0px;
  }
`;

const components = {
  Header() {
    return (
      <View style={{ justifyContent: 'center' }}>
        <View
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            background: 'transparent',
            marginLeft: '40px',
            marginTop: '32px',
          }}
        >
          <img src="/images/paiv-logo.png" alt="PAIV" style={{ height: 24 }} />
        </View>
        <View style={{ paddingTop: 22 }}>
          <Typography
            sx={{
              color: 'white',
              textAlign: 'left',
              fontSize: 32,
              fontFamily: 'Manrope',
              fontWeight: 'bold',
              marginLeft: '40px',
              letterSpacing: '-0.5px',
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{
              textAlign: 'left',
              fontSize: 15,
              fontFamily: 'Manrope',
              marginLeft: '40px',
              color: '#9ca3af',
            }}
          >
            Log in to your account
          </Typography>
        </View>
      </View>
    );
  },
};

global.toast = toast;
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

class Application extends React.Component {
  constructor(props) {
    super(props);
    // NOTE: this.state.isLogged is set in ApplicationStateBootstrap which is why this is passed to that component as a prop. This state variable is needed to dynamically change the app's styliing for when a user isn't logged in (log in screen) vs. when a user is logged in (rest of the app)
    this.state = {
      isLoggedIn: !!global.me, // Initial state based on global.me
    };
  }

  render() {
    // console.log(
    //   'LOG IN STYLING this.state?.isLoggedIn: ',
    //   this.state?.isLoggedIn
    // );

    return (
      <Box
        style={
          this.state?.isLoggedIn
            ? {}
            : {
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '32px',
                backgroundColor: '#0a0a0a',
                padding: '24px',
              }
        }
        sx={
          !this.state?.isLoggedIn
            ? {
                flexDirection: { xs: 'column', md: 'row' },
                '& > *': { margin: '0 auto' },
              }
            : {}
        }
      >
        <QueryClientProvider client={queryClient}>
          <Box
            style={
              this.state?.isLoggedIn
                ? {}
                : {
                    border: '1px solid #3a3a3a',
                    borderRadius: 20,
                    width: '100%',
                    maxWidth: '460px',
                    minHeight: '480px',
                    backgroundColor: '#111111',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }
            }
          >
            <Authenticator
              components={components}
              loginMechanisms={['email']}
              hideSignUp={true}
            >
              <PermissionsProvider>
                <Provider store={store}>
                  <ThemeProvider>
                    <CustomThemeProvider>
                      <Suspense fallback={<CenteredProgress />}>
                        <ApplicationStateBootstrap this={this}>
                          <Box>
                            <RouteConfiguration />
                          </Box>
                          <StyledToastContainer />
                        </ApplicationStateBootstrap>
                      </Suspense>
                    </CustomThemeProvider>
                  </ThemeProvider>
                </Provider>
              </PermissionsProvider>
            </Authenticator>
          </Box>
          {!this.state?.isLoggedIn && (
            <Box
              sx={{
                border: '1px solid #2a2a2a',
                borderRadius: '20px',
                padding: '32px',
                width: '400px',
                display: { xs: 'none', lg: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#111111',
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: 24,
                  fontFamily: 'Manrope',
                  fontWeight: 'bold',
                  marginBottom: '24px',
                  textAlign: 'center',
                }}
              >
                Download the Paiv App
              </Typography>

              <Box
                style={{
                  display: 'flex',
                  gap: '24px',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <Box
                    style={{
                      width: '150px',
                      height: '150px',
                      border: '1px solid #D7D9D6',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'white',
                    }}
                  >
                    <img
                      src="https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=https://apps.apple.com/us/app/paiv-win-on-the-go/id1577406696"
                      alt="App Store QR Code"
                      style={{ width: '140px', height: '140px' }}
                    />
                  </Box>
                  <a
                    href="https://apps.apple.com/us/app/paiv-win-on-the-go/id1577406696"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <img
                      src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                      alt="Download on App Store"
                      style={{ height: '40px', cursor: 'pointer' }}
                    />
                  </a>
                </Box>

                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <Box
                    style={{
                      width: '150px',
                      height: '150px',
                      border: '1px solid #D7D9D6',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'white',
                    }}
                  >
                    <img
                      src="https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=https://play.google.com/store/apps/details?id=com.gogamify.app"
                      alt="Google Play QR Code"
                      style={{ width: '140px', height: '140px' }}
                    />
                  </Box>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.gogamify.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                      alt="Get it on Google Play"
                      style={{ height: '40px', cursor: 'pointer' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML =
                          '<div style="background: black; color: white; padding: 8px 16px; border-radius: 4px; font-family: Manrope; font-size: 14px; cursor: pointer;">GET IT ON<br/><strong>Google Play</strong></div>';
                      }}
                    />
                  </a>
                </Box>
              </Box>
            </Box>
          )}
          {/* Mobile app download - shows store buttons directly (no QR) */}
          {!this.state?.isLoggedIn && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                marginTop: '8px',
              }}
            >
              <Typography
                sx={{
                  color: '#9ca3af',
                  fontSize: 14,
                  fontFamily: 'Manrope',
                  textAlign: 'center',
                }}
              >
                Get the PAIV mobile app
              </Typography>
              <Box sx={{ display: 'flex', gap: '12px' }}>
                <a
                  href="https://apps.apple.com/us/app/paiv-win-on-the-go/id1577406696"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download on App Store"
                    style={{ height: '36px' }}
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.gogamify.app"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Get it on Google Play"
                    style={{ height: '36px' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </a>
              </Box>
            </Box>
          )}
        </QueryClientProvider>
      </Box>
    );
  }
}

export default Application;
